import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import {
  applyRound,
  COMPUTED_SKIP,
  evaluateExpr,
  formatComputedCellValue,
  formatComputedFieldValue,
  parseNumericValue,
  selectRule,
} from './computedValue';
import {
  buildComputedGraph,
  cellValueKey,
  extractInitialCellValues,
  getTargetKeysForTags,
  targetKeyToVisitedId,
} from './computedGraph';
import { encodeFieldName, decodeFieldName } from './fieldNameCodec';
import { matchesAnyCondition } from './formUseCase';

/**
 * @typedef {object} FormComputedApi
 * @property {(rowId: string, colId: string) => string | undefined} getCellValue
 * @property {(row: FormTableRow) => boolean} isRowVisible
 */

/**
 * @param {ApiFormResponse | null | undefined} formConfig
 * @param {boolean} isLoading
 * @param {import('react-hook-form').UseFormWatch<Record<string, unknown>>} watch
 * @param {import('react-hook-form').UseFormGetValues<Record<string, unknown>>} getValues
 * @param {import('react-hook-form').UseFormSetValue<Record<string, unknown>>} setValue
 * @param {Record<string, FormItem>} fieldsById
 * @param {(row: FormTableRow, formValues: Record<string, unknown>) => boolean} isRowVisibleFn
 * @returns {FormComputedApi}
 */
function useComputedValues(
  formConfig,
  isLoading,
  watch,
  getValues,
  setValue,
  fieldsById,
  isRowVisibleFn,
) {
  const graph = useMemo(
    () => (formConfig && !isLoading ? buildComputedGraph(formConfig) : null),
    [formConfig, isLoading],
  );

  const [cellValues, setCellValues] = useState(() => (
    formConfig && !isLoading ? extractInitialCellValues(formConfig) : {}
  ));

  useEffect(() => {
    if (formConfig && !isLoading) {
      setCellValues(extractInitialCellValues(formConfig));
    }
  }, [formConfig, isLoading]);

  const cellValuesRef = useRef(cellValues);
  cellValuesRef.current = cellValues;

  const isPropagatingRef = useRef(false);

  const getCellValue = useCallback((rowId, colId) => {
    const key = cellValueKey(rowId, colId);
    return cellValuesRef.current[key];
  }, []);

  const getFieldRawValue = useCallback((apiId) => {
    const raw = getValues(encodeFieldName(apiId));
    if (raw === null || raw === undefined) return '';
    return String(raw);
  }, [getValues]);

  const getFieldNumericValue = useCallback((apiId) => (
    parseNumericValue(getFieldRawValue(apiId))
  ), [getFieldRawValue]);

  const getTagValues = useCallback((tag, filter) => {
    if (!graph) return [];
    const entries = graph.tagIndex.get(tag) ?? [];
    return entries
      .map((entry) => {
        if (filter) {
          const filterRowId = entry.kind === 'field' ? entry.rowId : entry.rowId;
          if (!filterRowId) return undefined;
          const filterColId = filter.colId;
          const filterFieldIds = graph.fieldsByColId.get(filterColId);
          if (!filterFieldIds) return undefined;
          const filterFieldInRow = [...filterFieldIds].find(
            (fieldId) => graph.fieldRowIdByFieldId.get(fieldId) === filterRowId,
          );
          if (!filterFieldInRow) return undefined;
          const filterValue = getFieldRawValue(filterFieldInRow);
          if (String(filterValue) !== String(filter.equals)) return undefined;
        }

        if (entry.kind === 'field') {
          return parseNumericValue(getFieldRawValue(entry.id));
        }
        const cellVal = cellValuesRef.current[cellValueKey(entry.rowId, entry.colId)];
        return parseNumericValue(cellVal);
      })
      .filter((v) => v !== undefined);
  }, [graph, getFieldRawValue]);

  const evaluateTarget = useCallback((targetKey, formValues) => {
    if (!graph) return COMPUTED_SKIP;
    const target = graph.targets.get(targetKey);
    if (!target) return COMPUTED_SKIP;

    const matchWhen = (conditions) => matchesAnyCondition(
      conditions,
      formValues,
      fieldsById,
    );

    const rule = selectRule(target.rules, matchWhen);
    if (!rule) return COMPUTED_SKIP;

    const result = evaluateExpr(rule.expression, {
      getFieldNumericValue,
      getFieldRawValue,
      getTagValues,
      onDivideByZero: rule.onDivideByZero ?? 'skip',
    });

    if (result === COMPUTED_SKIP || result === undefined) return COMPUTED_SKIP;
    const scalar = Array.isArray(result)
      ? result.reduce((sum, item) => sum + (Number.isFinite(item) ? item : 0), 0)
      : result;
    const rounded = applyRound(scalar, rule.round ?? null);
    return rounded;
  }, [graph, fieldsById, getFieldNumericValue, getFieldRawValue, getTagValues]);

  const applyTargetResult = useCallback((targetKey, value) => {
    if (!graph) return [];
    const target = graph.targets.get(targetKey);
    if (!target) return [];

    /** @type {string[]} */
    const affectedTags = [];

    if (target.kind === 'field') {
      const formatted = formatComputedFieldValue(value, target.field);
      setValue(encodeFieldName(target.id), formatted, {
        shouldValidate: false,
        shouldDirty: true,
      });
      (target.field.text?.tags ?? []).forEach((tag) => affectedTags.push(tag));
      return affectedTags;
    }

    const formatted = formatComputedCellValue(value, target.cellType);
    const key = cellValueKey(target.rowId, target.colId);
    cellValuesRef.current = { ...cellValuesRef.current, [key]: formatted };
    setCellValues(cellValuesRef.current);
    return target.tags ?? [];
  }, [graph, setValue]);

  const recomputeFrom = useCallback((changedApiId, visited = new Set()) => {
    if (!graph || !formConfig) return;
    if (visited.has(changedApiId)) return;
    visited.add(changedApiId);

    const queue = [...(graph.dependents.get(changedApiId) ?? [])];

    while (queue.length > 0) {
      const targetKey = queue.shift();
      if (!targetKey) {
        // skip empty queue entries
      } else {
        const visitedId = targetKeyToVisitedId(targetKey);
        if (!visited.has(visitedId)) {
          visited.add(visitedId);

          const formValues = getValues();
          const result = evaluateTarget(targetKey, formValues);
          if (result !== COMPUTED_SKIP) {
            const affectedTags = applyTargetResult(targetKey, result);

            const target = graph.targets.get(targetKey);
            if (target?.kind === 'field') {
              const nextDeps = graph.dependents.get(target.id) ?? new Set();
              nextDeps.forEach((dep) => {
                if (!visited.has(targetKeyToVisitedId(dep))) queue.push(dep);
              });
            }

            getTargetKeysForTags(affectedTags, graph).forEach((dep) => {
              if (!visited.has(targetKeyToVisitedId(dep))) queue.push(dep);
            });
          }
        }
      }
    }
  }, [graph, formConfig, getValues, evaluateTarget, applyTargetResult]);

  useEffect(() => {
    if (!graph || isLoading) return undefined;

    const subscription = watch((_values, { name, type }) => {
      if (type !== 'change' || !name || isPropagatingRef.current) return;
      isPropagatingRef.current = true;
      try {
        recomputeFrom(decodeFieldName(name));
      } finally {
        isPropagatingRef.current = false;
      }
    });

    return () => subscription.unsubscribe();
  }, [watch, graph, isLoading, recomputeFrom]);

  const isRowVisible = useCallback((row) => {
    const formValues = getValues();
    return isRowVisibleFn(row, formValues);
  }, [getValues, isRowVisibleFn]);

  return {
    getCellValue,
    isRowVisible,
  };
}

export default useComputedValues;
